import React, { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Seo, { breadcrumbJsonLd } from '../components/Seo.jsx';
import { BlogHero, PostGrid, LoadMore, RssLink } from '../components/blog/BlogParts.jsx';
import { blogApi, PATHS } from '../lib/opinly';

export function usePosts(filter) {
  const [state, setState] = useState({ posts: [], cursor: null, hasMore: false, loading: true, error: null });
  const key = JSON.stringify(filter);

  const load = useCallback(async (cursor) => {
    const params = JSON.parse(key);
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const res = await blogApi.posts({ ...params, cursor });
      setState((s) => ({ posts: cursor ? [...s.posts, ...res.data] : res.data, cursor: res.next_cursor, hasMore: res.has_more, loading: false, error: null }));
    } catch (error) {
      setState((s) => ({ ...s, loading: false, error }));
    }
  }, [key]);

  useEffect(() => { load(undefined); }, [load]);
  return { ...state, loadMore: () => load(state.cursor), retry: () => load(undefined) };
}

export default function Blog() {
  const [params, setParams] = useSearchParams();
  const sort = params.get('sort') === 'oldest' ? 'oldest' : 'newest';
  const { posts, hasMore, loading, error, loadMore, retry } = usePosts({ sort });
  const [categories, setCategories] = useState([]);

  useEffect(() => { blogApi.categories().then(setCategories).catch(() => setCategories([])); }, []);

  return (
    <div>
      <Seo
        title="Blog – Lerntipps & Nachhilfe-Wissen"
        description="Lerntipps, Motivation, Prüfungsvorbereitung und Neuigkeiten von AvantiTutor – dem Online-Nachhilfe-Team für Grundschule bis Uni."
        path={PATHS.index}
        jsonLd={[breadcrumbJsonLd([{ name: 'Startseite', path: '/' }, { name: 'Blog', path: PATHS.index }]), { '@context': 'https://schema.org', '@type': 'Blog', name: 'AvantiTutor Blog', url: `${window.location.origin}${PATHS.index}` }]}
      />
      <BlogHero title="Lerntipps, Wissen & Neuigkeiten" description="Praktische Tipps für Schüler, Studierende und Eltern – aus dem Alltag unserer Tutoren." crumbs={[{ name: 'Startseite', path: '/' }, { name: 'Blog' }]}>
        {categories.length > 0 && (
          <div data-testid="blog-categories" className="mt-8 flex flex-wrap gap-2">
            {categories.map((c) => (
              <Link key={c.slug} to={PATHS.category(c.slug)} data-testid={`blog-category-chip-${c.slug}`} className="px-4 py-2 rounded-full text-sm font-semibold bg-white text-slate-700 border border-slate-200 hover:border-teal-300 transition-colors">{c.title}</Link>
            ))}
          </div>
        )}
      </BlogHero>

      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
            <div role="group" aria-label="Sortierung" className="inline-flex rounded-full border border-slate-200 p-1 bg-white">
              {[['newest', 'Neueste'], ['oldest', 'Älteste']].map(([v, label]) => (
                <button key={v} data-testid={`blog-sort-${v}`} onClick={() => setParams(v === 'newest' ? {} : { sort: v })} aria-pressed={sort === v} className={`px-4 h-9 rounded-full text-sm font-semibold transition-colors ${sort === v ? 'text-white' : 'text-slate-600 hover:text-slate-900'}`} style={sort === v ? { background: '#3d94b8' } : {}}>{label}</button>
              ))}
            </div>
            <div className="flex items-center gap-5">
              <Link to={PATHS.authors} className="text-sm font-semibold hover:underline" style={{ color: '#2d7d9a' }}>Unsere Autoren</Link>
              <RssLink />
            </div>
          </div>
          <PostGrid posts={posts} loading={loading} error={error} onRetry={retry} featuredFirst />
          <LoadMore hasMore={hasMore} loading={loading} onClick={loadMore} />
        </div>
      </section>
    </div>
  );
}
